/**
 * Media Upload Configuration
 * 
 * This file contains configuration for media uploads, using the Path Registry system
 * to dynamically determine default values, required parameters, and critical parameters
 * for different resource types.
 */

import { getPathDefinition } from './pathRegistryUtils';

/**
 * Get default parameters for a resource type from the Path Registry
 * @param {string} resourceType - The type of resource
 * @returns {Promise<Object>} - The default parameters
 */
export const getResourceTypeDefaults = async (resourceType) => {
  const pathDef = await getPathDefinition(resourceType);

  if (!pathDef) {
    console.warn(`Path definition for ${resourceType} not found`);
    return {};
  }

  // Extract defaults from path definition
  const defaults = {};
  pathDef.params.forEach(param => {
    if (param.defaultValue !== undefined) {
      defaults[param.name] = param.defaultValue;
    }
  });

  // If folderName is not set but we have a pattern with a static folder name, use it
  if (!defaults.folderName && pathDef.pattern && pathDef.pattern.folderTemplate) {
    // Look for common folder names in the template
    const commonFolderNames = ['profile-img', 'avatar', 'covers', 'marketing', 'images', 'audio'];
    for (const name of commonFolderNames) {
      if (pathDef.pattern.folderTemplate.includes(name)) {
        defaults.folderName = name;
        break;
      }
    }
  }

  return defaults;
};

/**
 * Apply default parameters based on resource type
 * @param {string} resourceType - The type of resource
 * @param {Object} params - The parameters provided by the caller
 * @returns {Promise<Object>} - The parameters with defaults applied
 */
export const applyDefaultParams = async (resourceType, params = {}) => {
  // Get defaults for this resource type
  const defaults = await getResourceTypeDefaults(resourceType);

  // Merge defaults with provided params (provided params take precedence)
  return {
    ...defaults,
    ...params
  };
};

/**
 * Get required parameters for a resource type from the Path Registry
 * @param {string} resourceType - The type of resource
 * @returns {Promise<Array<string>>} - Array of required parameter names
 */
export const getRequiredParams = async (resourceType) => {
  const pathDef = await getPathDefinition(resourceType);

  if (!pathDef) {
    console.warn(`Path definition for ${resourceType} not found`);
    return ['userId', 'fileName']; // Minimum required parameters
  }

  // Extract required parameters from the path definition
  const requiredParams = pathDef.params
    .filter(param => param.required)
    .map(param => param.name);

  // Always include userId and fileName as required
  if (!requiredParams.includes('userId')) {
    requiredParams.push('userId');
  }
  if (!requiredParams.includes('fileName')) {
    requiredParams.push('fileName');
  }

  return requiredParams;
};

/**
 * Get critical parameters for a resource type from the Path Registry
 * @param {string} resourceType - The type of resource
 * @returns {Promise<Array<string>>} - Array of critical parameter names
 */
export const getCriticalParams = async (resourceType) => {
  const pathDef = await getPathDefinition(resourceType);

  if (!pathDef) {
    console.warn(`Path definition for ${resourceType} not found`);
    return ['resourceType', 'userId']; // Minimum critical parameters
  }

  // Always include resourceType and userId as critical
  const criticalParams = ['resourceType', 'userId'];

  // Add parameters marked as critical in the path definition
  pathDef.params
    .filter(param => param.isCritical)
    .forEach(param => {
      if (!criticalParams.includes(param.name)) {
        criticalParams.push(param.name);
      }
    });

  return criticalParams;
};

/**
 * Validate parameters for a resource type
 * @param {string} resourceType - The type of resource
 * @param {Object} params - The parameters to validate
 * @returns {Promise<Object>} - Validation result {valid: boolean, missing: string[]}
 */
export const validateParams = async (resourceType, params) => {
  const requiredParams = await getRequiredParams(resourceType);
  const missing = requiredParams.filter(param => !params[param]);

  return {
    valid: missing.length === 0,
    missing
  };
};
